import React from "react";
import { HashRouter as Router } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material";
import { UseCasesProvider, type UseCases } from "@gui/contexts/useCases/UseCasesContext";
import { authUseCases } from "@gui/infrastructure/composition/auth";
import { companiesUseCases } from "@gui/infrastructure/composition/companies";
import { importSettingsUseCases } from "@gui/infrastructure/composition/imports";
import { integrationsUseCases } from "@gui/infrastructure/composition/integrations";
import { missionControlUseCases } from "@gui/infrastructure/composition/missionControl";
import { passwordGeneratorUseCases } from "@gui/infrastructure/composition/teamMembers";
import { labelsUseCases } from "@gui/infrastructure/composition/labels";
import { missionsUseCases } from "@gui/infrastructure/composition/missions";
import { duplicateDealsUseCases } from "@gui/infrastructure/composition/deals";
import { branchMatcherUseCases } from "@gui/infrastructure/composition/branchMatcher";
import { dealMatcherUseCases } from "@gui/infrastructure/composition/dealMatcher";
import { jobUseCases } from "@gui/infrastructure/composition/jobs";
import { UserContext } from "@gui/contexts/user/UserContext";
import { CompanyContextProvider } from "@gui/contexts/company";
import { IssueCountsProvider } from "@gui/contexts/issueCounts/IssueCountsContext";
import { User } from "@domain/Auth";
const useCases: UseCases = {
	auth: authUseCases,
	companies: companiesUseCases,
	jobs: jobUseCases,
	missions: missionsUseCases,
	branchMatcher: branchMatcherUseCases,
	dealMatcher: dealMatcherUseCases,
	importSettings: importSettingsUseCases,
	duplicateDeals: duplicateDealsUseCases,
	integrations: integrationsUseCases,
	missionControl: missionControlUseCases,
	passwordGenerator: passwordGeneratorUseCases,
	labels: labelsUseCases,
};

export const theme = createTheme({
	palette: {
		primary: {
			main: "#274052",
		},
		secondary: {
			main: "#dc004e",
		},
	},
});

interface AppProvidersProps {
	currentUser: User | undefined;
	children: React.ReactNode;
}

/**
 * Composition root for the app's React provider tree.
 *
 * Order is significant (outermost → innermost):
 * - UseCasesProvider: outermost so any consumer (including auth flows that run before a user
 *   exists) can invoke use cases without depending on UserContext.
 * - UserContext: above ThemeProvider so any consumer can read currentUser without a CompanyContext.
 * - ThemeProvider: wraps everything that uses MUI styling.
 * - Router: wraps everything that uses routing (must be inside ThemeProvider since some MUI
 *   components rely on the theme inside route elements).
 * - CompanyContextProvider: depends on currentUser and is consumed by feature pages, so it
 *   sits inside Router and UserContext.
 * - IssueCountsProvider: scoped to authenticated feature pages and may depend on the active
 *   company, so it sits innermost around children.
 */
export function AppProviders({ currentUser, children }: AppProvidersProps) {
	return (
		<UseCasesProvider value={useCases}>
			<UserContext.Provider value={currentUser}>
				<ThemeProvider theme={theme}>
					<Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
						<CompanyContextProvider user={currentUser}>
							<IssueCountsProvider>{children}</IssueCountsProvider>
						</CompanyContextProvider>
					</Router>
				</ThemeProvider>
			</UserContext.Provider>
		</UseCasesProvider>
	);
}
